import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  FileText, Search, Camera, Check, X, Download, ChevronLeft, ChevronRight,
  Upload, Image as ImageIcon, AlertTriangle, Loader2, ShieldCheck, Clock,
} from "lucide-react";

const PAGE_SIZE = 25;
function fmt(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString(); }
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function WaybillsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [signFor, setSignFor] = useState(null);
  const [viewSig, setViewSig] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "ok") => { setToast({ msg, kind }); setTimeout(() => setToast(null), 2400); };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (status !== "ALL") params.status = status;
      const r = await api.get("/waybills", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch {
      showToast("Failed to load waybills", "err");
    } finally {
      setLoading(false);
    }
  }, [skip, search, status]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setSkip(0); }, [search, status]);

  const downloadPdf = async (wbNo) => {
    try {
      const r = await api.get(`/waybills/${wbNo}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${wbNo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { showToast("Re-print failed", "err"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6" data-testid="waybills-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><FileText size={12} /> Waybills</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">
            All issued waybills.
          </h1>
          <p className="mt-2 text-slate-600">Re-print, capture driver signatures, lock once signed.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" data-testid="waybills-search" placeholder="Search WB no., container, plate, driver…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none" />
        </div>
        <select data-testid="waybills-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none bg-white">
          <option value="ALL">All statuses</option>
          <option value="PENDING">Pending signature</option>
          <option value="SIGNED">Signed</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="waybills-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
              <tr>
                <Th>Waybill No.</Th>
                <Th>Status</Th>
                <Th>Issued</Th>
                <Th>Container</Th>
                <Th>Driver / Plate</Th>
                <Th>Destination</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center" data-testid="waybills-empty">
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <FileText size={32} className="text-slate-300" />
                    <span className="font-semibold">No waybills match.</span>
                    <span className="text-xs">Issue one from Inspection → Records.</span>
                  </div>
                </td></tr>
              )}
              {!loading && items.map((row) => (
                <tr key={row.waybill_no} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`waybill-row-${row.waybill_no}`}>
                  <Td><span className="font-mono-tabular font-bold text-[#0a2952]">{row.waybill_no}</span></Td>
                  <Td><StatusBadge status={row.status} /></Td>
                  <Td>
                    <div className="text-xs text-slate-700">{fmtDate(row.issued_at)}</div>
                    <div className="text-[10px] text-slate-400">{row.issued_by_name}</div>
                  </Td>
                  <Td>
                    <span className="font-mono-tabular text-xs font-semibold text-[#0a2952]">{row.container_no}</span>
                    <span className="ml-2 text-[10px] text-slate-500">{row.type_size}</span>
                  </Td>
                  <Td>
                    <div className="text-xs text-slate-700">{row.driver_name || "—"}</div>
                    <div className="text-[10px] text-slate-400 font-mono-tabular">{row.truck_plate || "—"}</div>
                  </Td>
                  <Td><span className="text-xs text-slate-600">{row.destination || "—"}</span></Td>
                  <Td align="right">
                    <div className="flex items-center gap-1 justify-end">
                      {row.status === "SIGNED" ? (
                        <button type="button" data-testid={`view-sig-btn-${row.waybill_no}`}
                          onClick={() => setViewSig(row)}
                          className="inline-flex items-center gap-1 px-2.5 h-8 rounded text-xs font-bold uppercase tracking-wide text-[#15803d] hover:bg-[#22c55e]/10">
                          <ImageIcon size={13} /> View signature
                        </button>
                      ) : (
                        <button type="button" data-testid={`sign-btn-${row.waybill_no}`}
                          onClick={() => setSignFor(row)}
                          className="inline-flex items-center gap-1 px-2.5 h-8 rounded text-xs font-bold uppercase tracking-wide text-[#ff5b2c] hover:bg-[#ff5b2c]/10">
                          <Camera size={13} /> Capture signature
                        </button>
                      )}
                      <button type="button" data-testid={`reprint-btn-${row.waybill_no}`}
                        onClick={() => downloadPdf(row.waybill_no)}
                        className="w-8 h-8 grid place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-[#0a2952]" title="Re-print">
                        <Download size={14} />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-600 font-mono-tabular">
            {total === 0 ? "0 waybills" : `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${fmt(total)}`}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="wb-prev-page" onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} disabled={skip === 0}
              className="w-8 h-8 grid place-items-center rounded border border-slate-300 hover:border-[#0a2952] disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-semibold text-slate-700 px-2 font-mono-tabular">{currentPage} / {totalPages}</span>
            <button type="button" data-testid="wb-next-page" onClick={() => setSkip(Math.min((totalPages - 1) * PAGE_SIZE, skip + PAGE_SIZE))} disabled={currentPage >= totalPages}
              className="w-8 h-8 grid place-items-center rounded border border-slate-300 hover:border-[#0a2952] disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <SignatureUploadModal
        open={!!signFor}
        waybill={signFor}
        onClose={() => setSignFor(null)}
        onSuccess={(wbNo) => { showToast(`Signature saved · ${wbNo}`); fetchItems(); }}
      />
      <SignatureViewerModal open={!!viewSig} waybill={viewSig} onClose={() => setViewSig(null)} />

      {toast && (
        <div data-testid="toast" className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-md shadow-lg text-sm font-semibold ${
          toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
        }`}>{toast.msg}</div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "SIGNED") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-sm bg-[#22c55e]/15 text-[#15803d]">
        <ShieldCheck size={11} /> Signed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-sm bg-amber-50 text-amber-700">
      <Clock size={11} /> Pending
    </span>
  );
}

function Th({ children, align = "left" }) { return <th className={`px-4 py-3 text-${align} whitespace-nowrap`}>{children}</th>; }
function Td({ children, align = "left" }) { return <td className={`px-4 py-3 text-${align} whitespace-nowrap`}>{children}</td>; }

export function SignatureUploadModal({ open, waybill, onClose, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [bytes, setBytes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) { setPreview(null); setBytes(null); setError(null); }
  }, [open]);

  if (!open || !waybill) return null;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { setError("Image too large (max 6 MB)"); return; }
    setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUri = ev.target.result;
      setPreview(dataUri);
      setBytes(dataUri.split(",")[1]);
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!bytes) { setError("Pick a photo first"); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/waybills/${waybill.waybill_no}/sign`, { image_base64: bytes });
      onSuccess?.(waybill.waybill_no);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-3 sm:p-4 overflow-y-auto" data-testid="sig-upload-modal">
      <div className="bg-white w-full max-w-lg rounded-md border border-slate-200 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#ff5b2c]/10 grid place-items-center"><Camera className="text-[#ff5b2c]" size={18} /></div>
            <div>
              <div className="overline">Capture driver signature</div>
              <div className="font-display font-extrabold text-base text-[#0a2952]">
                <span className="font-mono-tabular">{waybill.waybill_no}</span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-sm font-normal">{waybill.container_no}</span>
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="sig-close-btn"
            className="w-9 h-9 grid place-items-center rounded text-slate-500 hover:bg-slate-100"><X size={16} /></button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-slate-600">
            Take a clear photo of the <strong>signed waybill paper</strong>. Once uploaded, the waybill will be marked
            <span className="font-bold text-[#15803d]"> Signed</span> and locked.
          </p>

          {preview ? (
            <div className="relative group">
              <img src={preview} alt="Signed waybill preview" className="w-full max-h-[420px] object-contain rounded-md border border-slate-200 bg-slate-50" />
              <button type="button" onClick={() => { setPreview(null); setBytes(null); fileRef.current && (fileRef.current.value = ""); }}
                data-testid="sig-clear-btn"
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase bg-white border border-slate-300 rounded hover:bg-slate-100">
                <X size={12} /> Re-take
              </button>
            </div>
          ) : (
            <label className="block">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={onFile}
                data-testid="sig-file-input"
                className="hidden"
              />
              <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-[#ff5b2c] hover:bg-[#ff5b2c]/5 transition-colors">
                <Camera size={36} className="mx-auto text-slate-400 mb-2" />
                <div className="font-semibold text-[#0a2952]">Tap to take a photo</div>
                <div className="text-xs text-slate-500 mt-1">or pick an existing image (JPEG / PNG / WEBP, max 6 MB)</div>
              </div>
            </label>
          )}

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2" data-testid="sig-error">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="sig-cancel-btn"
            className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold">Cancel</button>
          <button type="button" onClick={submit} disabled={!bytes || busy} data-testid="sig-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-50 text-white text-sm font-bold">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Uploading…" : "Upload & lock"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SignatureViewerModal({ open, waybill, onClose }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !waybill) { setSrc(null); return; }
    let cancel = false;
    let url = null;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/waybills/${waybill.waybill_no}/signature`, { responseType: "blob" });
        if (cancel) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; if (url) URL.revokeObjectURL(url); };
  }, [open, waybill]);

  if (!open || !waybill) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-3 sm:p-4" data-testid="sig-viewer-modal">
      <div className="bg-white w-full max-w-2xl rounded-md border border-slate-200 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="overline flex items-center gap-2"><ShieldCheck size={12} className="text-[#15803d]" /> Signed waybill</div>
            <div className="font-display font-extrabold text-base text-[#0a2952]">
              <span className="font-mono-tabular">{waybill.waybill_no}</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="text-sm font-normal">{waybill.container_no}</span>
            </div>
            {waybill.signed_at && (
              <div className="text-[11px] text-slate-500 mt-1">
                Signed {fmtDate(waybill.signed_at)} · captured by {waybill.signed_by_name || "—"}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} data-testid="sig-viewer-close"
            className="w-9 h-9 grid place-items-center rounded text-slate-500 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 bg-slate-50 min-h-[300px] grid place-items-center">
          {loading && <Loader2 className="animate-spin text-slate-400" size={24} />}
          {!loading && src && <img src={src} alt="Driver signed waybill" className="max-h-[70vh] object-contain rounded shadow-sm" />}
          {!loading && !src && <div className="text-slate-500 text-sm">No signature on file.</div>}
        </div>
      </div>
    </div>
  );
}
